/**
 * The bar above every page: the rail control, the trail, the role, the theme.
 *
 * Drawn as finixt-ui's PageHeader (52px, card surface, hairline below). The
 * leading control is whichever the width allows: the collapse toggle on a
 * desktop, the drawer button on a phone, nothing in between, where finixt-ui's
 * own breakpoint keeps the rail at glyphs.
 */
import { Breadcrumb, type Crumb } from '../../ui/Breadcrumb.js';
import { Badge, IconButton } from '../../ui/finixt.js';
import { Tooltip } from '../../ui/Tooltip.js';
import { ThemeToggle } from './ThemeToggle.js';

export interface HeaderProps {
  trail: Crumb[];
  /** The desktop collapse toggle. */
  toggle?: { collapsed: boolean; onToggle: () => void } | undefined;
  /** The phone drawer button. */
  onOpenDrawer?: (() => void) | undefined;
  /** The operator's platform role, shown as a badge. */
  role?: string | undefined;
}

export function Header({ trail, toggle, onOpenDrawer, role }: HeaderProps) {
  return (
    <header
      style={{
        height: 'var(--h-header)',
        flexShrink: 0,
        boxSizing: 'border-box',
        background: 'var(--surface-card)',
        borderBottom: '1px solid var(--border-hairline)',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '0 14px',
      }}
    >
      {onOpenDrawer ? (
        <Tooltip label="Open navigation" side="bottom">
          <IconButton
            icon="menu"
            size={30}
            tone="bare"
            onClick={onOpenDrawer}
            aria-label="Open navigation"
          />
        </Tooltip>
      ) : toggle ? (
        <Tooltip label={toggle.collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="bottom">
          <IconButton
            icon={toggle.collapsed ? 'left_panel_open' : 'left_panel_close'}
            size={30}
            tone="bare"
            onClick={toggle.onToggle}
            aria-label={toggle.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          />
        </Tooltip>
      ) : null}
      <Breadcrumb items={trail} />
      <div
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}
      >
        {role ? (
          <Badge tone="blue" shape="square" mono>
            {role}
          </Badge>
        ) : null}
        <ThemeToggle />
      </div>
    </header>
  );
}
